import { useReveal } from "@/hooks/useReveal";
import { useState } from "react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FinalCTA from "@/components/site/FinalCTA";
import ConsentGate from "@/components/site/ConsentGate";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Play,
  Shield,
  Leaf,
  UserCheck,
  Repeat,
  Handshake,
  Gem,
  TrendingUp,
  Lock,
  Eye,
  ShieldCheck,
  Clock,
  Cpu,
  ChevronDown,
  MessageCircle,
  FileText,
  Rocket,
  Users,
} from "lucide-react";
import { CALENDLY_URL } from "@/lib/constants";
import bewertungenHero from "@/assets/bewertungen-hero.webp";

const GOOGLE_REVIEW_URL = "https://www.google.com/maps/place/FINABLE+GmbH/@53.5841498,9.9642221,17z/data=!4m8!3m7!1s0x47b18fc680f0ab9d:0x12ddcae72344f7c8!8m2!3d53.5841466!4d9.966797!9m1!1b1!16s%2Fg%2F11vyxp4dz2?entry=ttu&g_ep=EgoyMDI2MDQyMC4wIKXMDSoASAFQAw%3D%3D";
const TRUSTPILOT_REVIEW_URL = "https://de.trustpilot.com/review/finable.de";

type Testimonial = {
  name: string;
  age: number;
  city: string;
  profession: string;
  quote: string;
  vimeoId?: string;
};

const testimonials: Testimonial[] = [
  {
    name: "Anja",
    age: 27,
    city: "München",
    profession: "Performance-Marketing-Managerin",
    quote:
      "FINABLE hat mir endlich Klarheit über meine Finanzen gegeben – ich weiß jetzt genau, wo mein Geld arbeitet.",
    vimeoId: "1057218477",
  },
  {
    name: "Moritz",
    age: 25,
    city: "Kiel",
    profession: "Orthopädietechnikmeister",
    quote:
      "Transparent, sympathisch und auf Augenhöhe – so habe ich Finanzberatung noch nie erlebt.",
    vimeoId: "1057219372",
  },
  {
    name: "Julia",
    age: 27,
    city: "Hamburg",
    profession: "Personalentwicklerin",
    quote:
      "Mit FINABLE habe ich eine Strategie, die wirklich zu mir passt – nachhaltig und zukunftssicher.",
    vimeoId: "1058326440",
  },
  {
    name: "Leife Hase",
    age: 30,
    city: "Hamburg",
    profession: "Unternehmensberater",
    quote:
      "Endlich verstehe ich, was mit meinem Geld passiert – und das ohne komplizierte Fachbegriffe.",
    vimeoId: "1058336063",
  },
  {
    name: "Ludwig Spaude",
    age: 26,
    city: "Berlin",
    profession: "Fotograf",
    quote:
      "Ich fühle mich rundum gut betreut. FINABLE denkt mit – und nicht nur an Provisionen.",
    vimeoId: "1074190202",
  },
  {
    name: "Max Hagen",
    age: 25,
    city: "Hamburg",
    profession: "Fahrradmechaniker",
    quote:
      "Die Beratung war so klar und persönlich, dass ich mich sofort wohlgefühlt habe.",
    vimeoId: "1058336891",
  },
  {
    name: "Marc Dittmann",
    age: 30,
    city: "Wien",
    profession: "Fitnesscoach",
    quote:
      "Nachhaltig investieren und trotzdem gute Renditen – genau das wollte ich, und FINABLE macht es möglich.",
    vimeoId: "1058336344",
  },
  {
    name: "Max Heilmann",
    age: 27,
    city: "Schwerin",
    profession: "Finanzbeamter",
    quote:
      "Schnell, ehrlich, kompetent. Ich kann FINABLE jedem in meinem Umfeld weiterempfehlen.",
    vimeoId: "1058327664",
  },
  {
    name: "Johannes Breidenbach",
    age: 31,
    city: "Stuttgart",
    profession: "Coach",
    quote:
      "Finanzen waren für mich immer ein Buch mit sieben Siegeln – heute habe ich endlich einen klaren Plan.",
    vimeoId: "1058335814",
  },
];

const trustFeatures = [
  { icon: Shield, title: "Unabhängige Beratung", desc: "Nicht an bestimmte Anbieter gebunden" },
  { icon: Leaf, title: "Nachhaltige Finanzstrategien", desc: "Investments für Umwelt und Gesellschaft" },
  { icon: UserCheck, title: "Individuelle Konzepte", desc: "Maßgeschneidert auf Deine Lebenssituation" },
  { icon: Repeat, title: "Langfristige Betreuung", desc: "Wir passen Deine Strategie laufend an" },
  { icon: Handshake, title: "Faire Zusammenarbeit", desc: "Dein Erfolg ist unser Ziel" },
  { icon: Gem, title: "Exklusive Anlageoptionen", desc: "Möglichkeiten die Banken nicht bieten" },
  { icon: TrendingUp, title: "Höchste Renditechancen", desc: "Balance zwischen Sicherheit und Rendite" },
  { icon: Lock, title: "Sicherheit und Stabilität", desc: "Bewährte Anlagestrategien" },
  { icon: Eye, title: "Maximale Transparenz", desc: "Keine versteckten Kosten" },
  { icon: ShieldCheck, title: "Risikominimierung", desc: "Durchdachte Risikostreuung" },
  { icon: Clock, title: "Zeitersparnis", desc: "Wir übernehmen die Arbeit für Dich" },
  { icon: Cpu, title: "Innovative Technologien", desc: "Moderne Tools für Deine Finanzen" },
];

function VideoPlaceholder({ vimeoId }: { vimeoId?: string }) {
  if (vimeoId) {
    return (
      <ConsentGate service="Vimeo">
        <iframe
          src={`https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0`}
          className="absolute inset-0 w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={`Vimeo Video ${vimeoId}`}
        />
      </ConsentGate>
    );
  }
  return (
    <div className="relative aspect-video w-full rounded-2xl bg-primary overflow-hidden shadow-elegant group">
      <div className="absolute inset-0 bg-gradient-radial opacity-50" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 rounded-full bg-accent/95 flex items-center justify-center shadow-glow group-hover:scale-110 transition-transform duration-300">
          <Play className="w-9 h-9 text-accent-foreground fill-current ml-1" />
        </div>
      </div>
      <div className="absolute bottom-4 right-4 px-3 py-1 rounded bg-black/40 backdrop-blur text-white text-xs font-medium">
        Video
      </div>
    </div>
  );
}

export default function Kundenstimmen() {
  useReveal();
  const [visibleCount, setVisibleCount] = useState(3);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Kundenstimmen – FINABLE Finanzberatung"
        description="500+ zufriedene Kunden. Echte Erfahrungen mit steueroptimierter Altersvorsorge und unabhängiger Finanzberatung."
        canonical="https://finable.de/kundenstimmen"
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-balance reveal">
                Schnelle Umsetzung und{" "}
                <span className="text-accent">großartige Ergebnisse</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/80 reveal">
                Exzellenter Service, schnelle Umsetzung, tolle Ergebnisse – absolut empfehlenswert und
                professionell!
              </p>

              <div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-4 reveal">
                <div className="px-6 py-3 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur">
                  <span className="font-display text-2xl font-extrabold text-accent">2400+</span>{" "}
                  <span className="text-white/80">Verträge</span>
                </div>
                <div className="px-6 py-3 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur">
                  <span className="font-display text-2xl font-extrabold text-accent">500+</span>{" "}
                  <span className="text-white/80">Zufriedene Kunden</span>
                </div>
              </div>

              <div className="mt-10 flex justify-center lg:justify-start reveal">
                <Button asChild variant="hero" size="xl">
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                    Jetzt Erstgespräch vereinbaren <ArrowRight className="w-5 h-5" />
                  </a>
                </Button>
              </div>
            </div>

            <div className="reveal hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-elegant border border-white/10">
                <img
                  src={bewertungenHero}
                  alt="FINABLE Berater bei der Arbeit am Laptop"
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reviews widgets — Google + Trustpilot */}
        <section className="py-20 md:py-28 bg-secondary/30">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center reveal">
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-balance">
                Was unsere Kunden sagen
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Echte Bewertungen auf Google und Trustpilot.
              </p>
            </div>

            <div className="mt-14 grid lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
              {/* Google */}
              <div className="reveal rounded-2xl bg-card border border-border shadow-card p-7 flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl font-bold tracking-tight">
                      <span className="text-[#4285F4]">G</span>
                      <span className="text-[#EA4335]">o</span>
                      <span className="text-[#FBBC05]">o</span>
                      <span className="text-[#4285F4]">g</span>
                      <span className="text-[#34A853]">l</span>
                      <span className="text-[#EA4335]">e</span>
                    </span>
                    <span className="text-sm text-muted-foreground">Reviews</span>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-extrabold text-primary leading-none">5.0</div>
                    <div className="flex gap-0.5 mt-1 text-[#FBBC05]">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i}>★</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">47 Bewertungen</div>

                <div className="mt-5 space-y-3 flex-1">
                  {[
                    {
                      n: "Mia Oels",
                      t: "Kompetente Beratung auf Augenhöhe mit viel Transparenz. Vorgehen werden genau besprochen und auf Fragen wird geduldig eingegangen. Sowohl menschlich als auch skill-technisch sehr versiert.",
                    },
                    { n: "Sally Fiedler", t: "Ein sehr höfliches und kompetentes Team." },
                    {
                      n: "Alexander Kähler",
                      t: "Herr Komminga berät mich im Bereich Finanzanlagen, sowie Versicherungen mit umfangreichem Fachwissen und Empathie. Er handelt stets in meinem Interesse und hat zu keiner Zeit Druck aufgebaut. Ich fühle mich sicher aufgehoben und würde eine Beratung durch Herrn Komminga jeder Zeit empfehlen! Vielen Dank!",
                    },
                  ].map((r, i) => (
                    <div key={i} className="rounded-xl border border-border p-4 bg-background">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-primary text-sm">{r.n}</span>
                        <span className="text-[#FBBC05] text-sm">★★★★★</span>
                      </div>
                      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{r.t}</p>
                    </div>
                  ))}
                </div>

                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl border border-border bg-background hover:bg-secondary/50 font-semibold text-primary transition-colors"
                >
                  Alle Google Bewertungen
                </a>
              </div>

              {/* Trustpilot */}
              <div className="reveal rounded-2xl bg-card border border-border shadow-card p-7 flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[#00B67A] text-2xl">★</span>
                    <span className="font-display text-xl font-bold text-primary">Trustpilot</span>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-extrabold text-primary leading-none">4,7</div>
                    <div className="text-xs font-semibold text-[#00B67A] mt-1 uppercase tracking-wider">
                      Hervorragend
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span
                      key={i}
                      className="w-7 h-7 bg-[#00B67A] text-white flex items-center justify-center text-sm rounded-sm"
                    >
                      ★
                    </span>
                  ))}
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  Basierend auf 127 Bewertungen
                </div>

                <div className="mt-5 space-y-3 flex-1">
                  {[
                    {
                      n: "Sarah Courte",
                      t: "Ich bin seit einiger Zeit bei Finable und absolut zufrieden. Besonders wichtig war mir, dass meine Geldanlage nicht nur rentabel, sondern auch nachhaltig ist – und genau darauf ist die Beratung hier ausgerichtet.",
                    },
                    {
                      n: "Julia Hinz",
                      t: "Ich bin von Anfang an zufriedene Kundin bei Finable, und fühl mich seit Tag eins sicher und kompetent beraten. Ich schätze es sehr, dass ich mich jederzeit, gerade auch bei kurzfristigen oder dringenden Themen, melden kann.",
                    },
                    {
                      n: "Sofie",
                      t: "Ich hatte kaum Ahnung vom Thema Finanzen oder Altersvorsorge und viele Fragen dazu, aber die Beratung von Jann war einfach großartig! Jede meiner Fragen wurde geduldig und verständlich beantwortet.",
                    },
                  ].map((r, i) => (
                    <div key={i} className="rounded-xl border border-border p-4 bg-background">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-primary text-sm">{r.n}</span>
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <span
                              key={j}
                              className="w-4 h-4 bg-[#00B67A] text-white text-[10px] flex items-center justify-center rounded-sm"
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{r.t}</p>
                    </div>
                  ))}
                </div>

                <a
                  href={TRUSTPILOT_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl border border-border bg-background hover:bg-secondary/50 font-semibold text-primary transition-colors"
                >
                  Alle Trustpilot Bewertungen
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Video testimonials zigzag */}
        <section className="py-20 md:py-28 bg-background">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center reveal">
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">
                Kundenstimmen
              </span>
              <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-balance">
                Echte Geschichten, echte Kunden.
              </h2>
            </div>

            <div className="mt-16 space-y-20 md:space-y-28">
              {testimonials.slice(0, visibleCount).map((t, i) => {
                const reverse = i % 2 === 1;
                // Only the initial 3 use the scroll-reveal observer; later items are
                // shown immediately when expanded (the observer only sees mount-time nodes).
                const revealClass = i < 3 ? "reveal" : "is-visible";
                return (
                  <div
                    key={i}
                    className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                      reverse ? "lg:[&>*:first-child]:order-2" : ""
                    }`}
                  >
                    <div className={revealClass}>
                      <VideoPlaceholder vimeoId={t.vimeoId} />
                    </div>
                    <div className={revealClass}>
                      <blockquote className="font-display text-2xl md:text-3xl font-bold text-primary leading-snug text-balance">
                        „{t.quote}"
                      </blockquote>
                      <div className="mt-6 pt-6 border-t border-border">
                        <div className="font-display text-xl font-bold text-primary">
                          {t.name}, {t.age}
                        </div>
                        <div className="mt-1 text-muted-foreground">
                          {t.city} · {t.profession}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {visibleCount < testimonials.length && (
              <div className="mt-16 flex justify-center reveal">
                <button
                  onClick={() =>
                    setVisibleCount((c) => Math.min(c + 3, testimonials.length))
                  }
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-card border border-border hover:border-accent hover:bg-accent/5 shadow-card hover:shadow-elegant font-display font-bold text-primary transition-all duration-300"
                >
                  <span>Weitere Videos laden</span>
                  <ChevronDown className="w-5 h-5 text-accent group-hover:translate-y-0.5 transition-transform" />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Trust features */}
        <section className="py-20 md:py-28 bg-secondary/40">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center reveal">
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-balance">
                Warum Kunden uns vertrauen
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                12 gute Gründe für FINABLE als Deinen Finanzpartner.
              </p>
            </div>

            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {trustFeatures.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={i}
                    className="reveal rounded-2xl bg-card border border-border p-6 shadow-card hover:shadow-elegant hover:border-accent/30 transition-all duration-300"
                    style={{ transitionDelay: `${(i % 3) * 80}ms` }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-bold text-primary">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process — vertical timeline variant */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial opacity-40 pointer-events-none" />
          <div className="container relative">
            <div className="max-w-3xl mx-auto text-center reveal">
              <span className="text-accent font-semibold uppercase tracking-wider text-sm">
                Unser Ablauf
              </span>
              <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-balance">
                In vier Schritten zu Deiner Strategie
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Klar, transparent und auf Augenhöhe – so läuft die Zusammenarbeit mit FINABLE ab.
              </p>
            </div>

            <div className="mt-16 max-w-3xl mx-auto relative">
              {/* vertical rail */}
              <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-accent/0 via-accent/40 to-accent/0" />

              <div className="space-y-10 md:space-y-14">
                {[
                  {
                    n: "01",
                    icon: MessageCircle,
                    title: "Kennenlerngespräch",
                    desc: "Wir hören Dir zu und analysieren Deine aktuelle Situation sowie Deine Ziele.",
                  },
                  {
                    n: "02",
                    icon: FileText,
                    title: "Konzeptvorstellung",
                    desc: "Du bekommst eine klare, individuelle Finanzstrategie – nachhaltig, transparent und auf Dich zugeschnitten.",
                  },
                  {
                    n: "03",
                    icon: Rocket,
                    title: "Umsetzung & Kontrolle",
                    desc: "Wir setzen die erarbeitete Strategie mit Dir um und kontrollieren diese.",
                  },
                  {
                    n: "04",
                    icon: Users,
                    title: "Langfristige Betreuung",
                    desc: "Wir begleiten Dich auf Deiner finanziellen Reise und sprechen regelmäßig über unsere Strategie.",
                  },
                ].map((s, i) => {
                  const Icon = s.icon;
                  const isRight = i % 2 === 1;
                  return (
                    <div
                      key={i}
                      className="reveal relative md:grid md:grid-cols-2 md:gap-10 items-stretch"
                      style={{ transitionDelay: `${i * 100}ms` }}
                    >
                      {/* node */}
                      <div className="absolute left-8 md:left-1/2 -translate-x-1/2 top-6 z-10">
                        <div className="w-5 h-5 rounded-full bg-accent shadow-glow ring-4 ring-background" />
                      </div>

                      {/* card — explicitly placed left or right, alternating */}
                      <div
                        className={`pl-20 md:pl-0 flex ${
                          isRight
                            ? "md:col-start-2 md:pl-12"
                            : "md:col-start-1 md:pr-12"
                        }`}
                      >
                        <div className="w-full h-[240px] flex flex-col rounded-2xl bg-card border border-border shadow-card hover:shadow-elegant hover:border-accent/40 transition-all duration-300 p-6 md:p-7">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-accent" />
                            </div>
                          </div>

                          <h3 className="mt-3 font-display text-xl font-bold text-primary">
                            {s.title}
                          </h3>
                          <p className="mt-2 text-muted-foreground leading-relaxed">
                            {s.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
